
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

interface Project {
  id: number;
  title: string;
  category: string;
  description: string;
  image: string;
  technologies: string[];
  link?: string;
}

export default function PortfolioSection() {
  const [activeFilter, setActiveFilter] = useState('Tümü');
  const [visibleProjects, setVisibleProjects] = useState<number[]>([]);

  const categories = ['Tümü', 'Web Tasarım', 'E-Ticaret', 'Mobil Uygulama', 'UI/UX'];

  const projects: Project[] = [
    {
      id: 1,
      title: 'TechCorp Kurumsal Web Sitesi',
      category: 'Web Tasarım',
      description: 'Modern ve profesyonel kurumsal web sitesi tasarımı',
      image: 'https://public.readdy.ai/ai/img_res/aaf23553787b77a6b701e42f32e219ec.jpg',
      technologies: ['React', 'TypeScript', 'Tailwind CSS', 'Next.js']
    },
    {
      id: 2,
      title: 'ShopMax E-Ticaret Platformu',
      category: 'E-Ticaret',
      description: 'Tam özellikli e-ticaret sitesi ve yönetim paneli',
      image: 'https://readdy.ai/api/search-image?query=Modern%20e-commerce%20website%20interface%20showing%20product%20listings%2C%20shopping%20cart%2C%20clean%20product%20pages%2C%20professional%20online%20store%20design%2C%20blue%20and%20white%20theme%2C%20user-friendly%20layout&width=400&height=300&seq=ecommerce-site&orientation=landscape',
      technologies: ['Node.js', 'MongoDB', 'Stripe', 'React']
    },
    {
      id: 3,
      title: 'HealthCare Mobil Uygulaması',
      category: 'Mobil Uygulama',
      description: 'Sağlık hizmetleri için mobil randevu uygulaması',
      image: 'https://readdy.ai/api/search-image?query=Healthcare%20mobile%20app%20interface%20on%20smartphone%20screens%2C%20medical%20appointment%20booking%20app%2C%20clean%20blue%20and%20white%20design%2C%20professional%20healthcare%20application%2C%20user-friendly%20medical%20interface&width=400&height=300&seq=health-app&orientation=landscape',
      technologies: ['React Native', 'Firebase', 'Redux', 'TypeScript']
    },
    {
      id: 4,
      title: 'FoodDelivery UI/UX Tasarımı',
      category: 'UI/UX',
      description: 'Yemek siparişi uygulaması için kullanıcı deneyimi tasarımı',
      image: 'https://readdy.ai/api/search-image?query=Food%20delivery%20app%20UI%20design%20mockups%2C%20modern%20restaurant%20ordering%20interface%2C%20clean%20mobile%20app%20screens%2C%20professional%20food%20app%20design%2C%20orange%20and%20blue%20color%20scheme%2C%20user%20experience%20design&width=400&height=300&seq=food-ui&orientation=landscape',
      technologies: ['Figma', 'Adobe XD', 'Prototyping', 'User Research']
    },
    {
      id: 5,
      title: 'EduPlatform Eğitim Sitesi',
      category: 'Web Tasarım',
      description: 'Online eğitim platformu ve öğrenci yönetim sistemi',
      image: 'https://readdy.ai/api/search-image?query=Online%20education%20platform%20website%20design%2C%20e-learning%20interface%20with%20course%20listings%2C%20student%20dashboard%2C%20modern%20educational%20technology%2C%20blue%20and%20green%20color%20scheme%2C%20clean%20academic%20design&width=400&height=300&seq=edu-platform&orientation=landscape',
      technologies: ['Vue.js', 'Laravel', 'MySQL', 'WebRTC']
    },
    {
      id: 6,
      title: 'Fashion Store E-Ticaret',
      category: 'E-Ticaret',
      description: 'Moda markası için premium e-ticaret çözümü',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20fashion%20e-commerce%20website%20design%2C%20elegant%20clothing%20store%20interface%2C%20premium%20fashion%20brand%20website%2C%20sophisticated%20black%20and%20gold%20design%2C%20high-end%20retail%20experience&width=400&height=300&seq=fashion-store&orientation=landscape',
      technologies: ['Shopify', 'Liquid', 'JavaScript', 'CSS3']
    }
  ];

  const filteredProjects = activeFilter === 'Tümü' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const projectId = parseInt(entry.target.getAttribute('data-project-id') || '0');
            setVisibleProjects(prev => [...prev.filter(id => id !== projectId), projectId]);
          }
        });
      },
      { threshold: 0.1 }
    );

    const projects = document.querySelectorAll('[data-project-id]');
    projects.forEach(project => observer.observe(project));

    return () => observer.disconnect();
  }, [filteredProjects]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="referanslar" className="py-20 bg-gradient-to-br from-gray-800 via-gray-900 to-black relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/3 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full border border-blue-400/30 mb-6">
            <i className="ri-gallery-line text-blue-400 mr-2"></i>
            <span className="text-blue-400 text-sm font-medium">Referanslarımız</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Başarılı Projelerimiz
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Farklı sektörlerden müşterilerimiz için geliştirdiğimiz başarılı projeleri 
            inceleyin ve bizimle çalışmanın avantajlarını keşfedin.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveFilter(category)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeFilter === category
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                  : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 hover:text-white border border-gray-700/50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              data-project-id={project.id}
              className={`group relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 hover:border-blue-500/50 overflow-hidden transition-all duration-500 transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20 ${
                visibleProjects.includes(project.id)
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-10 opacity-0'
              }`}
              style={{
                transitionDelay: `${index * 150}ms`
              }}
            >
              {/* Project Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4 px-3 py-1 bg-blue-500/20 backdrop-blur-sm rounded-full border border-blue-400/30">
                  <span className="text-blue-400 text-xs font-medium">{project.category}</span>
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Button variant="outline" size="sm" className="bg-white/10 backdrop-blur-sm">
                    <i className="ri-eye-line mr-2"></i>
                    Detayları Gör
                  </Button>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors duration-300">
                  {project.title}
                </h3>
                
                <p className="text-gray-300 mb-4 leading-relaxed">
                  {project.description}
                </p>

                {/* Technologies */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.technologies.map((tech, idx) => (
                    <span 
                      key={idx}
                      className="px-2 py-1 bg-gray-700/50 text-gray-400 text-xs rounded-lg border border-gray-600/50"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Project Link */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Proje Detayları</span>
                  <i className="ri-arrow-right-line text-blue-400 transition-transform group-hover:translate-x-1"></i>
                </div>
              </div>

              {/* Glow Effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/0 via-cyan-500/0 to-blue-500/0 group-hover:from-blue-500/10 group-hover:via-cyan-500/10 group-hover:to-blue-500/10 transition-all duration-500 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl border border-blue-400/20 p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Siz de Projelerimize Katılın
            </h3>
            <p className="text-gray-300 mb-6">
              Başarılı referanslarımıza bir yenisini daha eklemek için 
              hemen iletişime geçin ve projenizi konuşalım.
            </p>
            <Button 
              size="lg"
              onClick={() => scrollToSection('iletisim')}
            >
              Projenizi Başlatalım
              <i className="ri-rocket-line ml-2"></i>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
